"use client"

import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import EditIcon from "@mui/icons-material/Edit"
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd"
import ScheduleIcon from "@mui/icons-material/Schedule"
import AvTimerIcon from "@mui/icons-material/AvTimer"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import { ThemeProvider } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import CustomPagination from "../transaccionComponent/CustomPagination"
import ModalRrhhTurno from "./ModalRrhhTurno.js"
import ModalRrhhAsignacionTurno from "./ModalRrhhAsignacionTurno.js"
import {
  extractErrorMessage,
  formatHora,
  rrhhTheme,
} from "./rrhhUi.js"

const PAGE_SIZE = 10

const TablaRrhhTurnosComponent = () => {
  const {
    getRrhhTurnos,
    getRrhhTurnoById,
    postRrhhTurno,
    updateRrhhTurno,
    getRrhhEmpleados,
    postRrhhAsignacionTurno,
    getRrhhAsignacionesTurnoByEmpleado,
  } = useContext(ErpContext)

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [searchText, setSearchText] = useState("")

  const [turnosOptions, setTurnosOptions] = useState([])
  const [empleadosOptions, setEmpleadosOptions] = useState([])

  const [turnoModalOpen, setTurnoModalOpen] = useState(false)
  const [turnoModalMode, setTurnoModalMode] = useState("create")
  const [selectedTurno, setSelectedTurno] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  const [asignacionModalOpen, setAsignacionModalOpen] = useState(false)
  const [defaultTurnoAsignacion, setDefaultTurnoAsignacion] = useState(null)

  const loadTurnosOptions = async () => {
    try {
      const response = await getRrhhTurnos({ page: 1, limit: 200 })
      setTurnosOptions(response?.data || [])
    } catch (loadError) {
      console.error("Error cargando catálogo de turnos RRHH:", loadError)
    }
  }

  const loadEmpleadosOptions = async () => {
    try {
      const response = await getRrhhEmpleados({ page: 1, limit: 200, estadoLaboral: "ACTIVO" })
      setEmpleadosOptions(response?.data || [])
    } catch (loadError) {
      console.error("Error cargando catálogo de empleados RRHH:", loadError)
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await getRrhhTurnos({
        page,
        limit: PAGE_SIZE,
        search: searchText.trim() || undefined,
      })

      setRows(response?.data || [])
      setTotalPages(response?.totalPages || 1)
      setTotalCount(response?.total || 0)
      setError(null)
    } catch (loadError) {
      setError(extractErrorMessage(loadError, "No se pudieron cargar los turnos RRHH."))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [page, searchText])

  useEffect(() => {
    loadTurnosOptions()
    loadEmpleadosOptions()
  }, [])

  const loadAsignacionesEmpleado = useCallback(async (idEntidad) => {
    return await getRrhhAsignacionesTurnoByEmpleado(idEntidad)
  }, [getRrhhAsignacionesTurnoByEmpleado])

  const handleRefresh = async () => {
    setError(null)
    setSuccess(null)
    await Promise.all([loadData(), loadTurnosOptions(), loadEmpleadosOptions()])
  }

  const handleOpenCreate = () => {
    setError(null)
    setSuccess(null)
    setSelectedTurno(null)
    setTurnoModalMode("create")
    setTurnoModalOpen(true)
  }

  const handleOpenEdit = async (idTurno) => {
    setError(null)
    setSuccess(null)
    setLoadingDetail(true)
    setTurnoModalMode("edit")
    setTurnoModalOpen(true)

    try {
      const detail = await getRrhhTurnoById(idTurno)
      setSelectedTurno(detail)
    } catch (detailError) {
      setError(extractErrorMessage(detailError, "No se pudo cargar el turno seleccionado."))
      setTurnoModalOpen(false)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleSaveTurno = async (payload) => {
    if (turnoModalMode === "edit") {
      await updateRrhhTurno(selectedTurno.id, payload)
      setSuccess("Turno actualizado correctamente.")
    } else {
      await postRrhhTurno(payload)
      setSuccess("Turno creado correctamente.")
    }

    setTurnoModalOpen(false)
    await Promise.all([loadData(), loadTurnosOptions()])
  }

  const handleOpenAsignacion = (turno = null) => {
    setError(null)
    setSuccess(null)
    setDefaultTurnoAsignacion(turno)
    setAsignacionModalOpen(true)
  }

  const handleSaveAsignacion = async (payload) => {
    await postRrhhAsignacionTurno(payload)
    setSuccess("Asignación de turno registrada correctamente.")
    setAsignacionModalOpen(false)
  }

  const promedioHorasPagina = useMemo(() => {
    if (rows.length === 0) return 0
    const total = rows.reduce((acc, row) => acc + (Number(row.horasBaseDiarias) || 0), 0)
    return total / rows.length
  }, [rows])

  const columns = [
    {
      field: "codigo",
      headerName: "Código",
      minWidth: 120,
      flex: 0.8,
      valueGetter: (_, row) => row.codigo || "-",
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      minWidth: 220,
      flex: 1.5,
    },
    {
      field: "horaEntrada",
      headerName: "Entrada",
      minWidth: 110,
      flex: 0.7,
      valueFormatter: (value) => formatHora(value),
    },
    {
      field: "horaSalida",
      headerName: "Salida",
      minWidth: 110,
      flex: 0.7,
      valueFormatter: (value) => formatHora(value),
    },
    {
      field: "minutosDescanso",
      headerName: "Descanso",
      minWidth: 110,
      flex: 0.7,
      valueGetter: (_, row) => `${row.minutosDescanso || 0} min`,
    },
    {
      field: "minutosToleranciaIngreso",
      headerName: "Tolerancia",
      minWidth: 110,
      flex: 0.7,
      valueGetter: (_, row) => `${row.minutosToleranciaIngreso || 0} min`,
    },
    {
      field: "horasBaseDiarias",
      headerName: "Horas base",
      minWidth: 110,
      flex: 0.7,
      valueGetter: (_, row) => row.horasBaseDiarias || "-",
    },
    {
      field: "usuarioCreador",
      headerName: "Creado por",
      minWidth: 180,
      flex: 1,
      valueGetter: (_, row) => row.usuarioCreador?.usuario || row.usuarioCreador?.nombre || "-",
    },
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar turno">
            <IconButton size="small" onClick={() => handleOpenEdit(row.id)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Asignar turno">
            <IconButton size="small" onClick={() => handleOpenAsignacion(row)}>
              <AssignmentIndIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              border: "1px solid rgba(15, 23, 42, 0.08)",
              background: "linear-gradient(135deg, #ffffff 0%, #eef6ff 100%)",
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#1f2937" }}>
                  Turnos RRHH
                </Typography>
                <Typography variant="body1" sx={{ color: "#4b5563", mt: 0.5 }}>
                  Plantillas horarias y asignación operacional por empleado.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                <Chip icon={<ScheduleIcon />} label={`${totalCount} turnos`} variant="outlined" />
                <Chip
                  icon={<AvTimerIcon />}
                  label={`Promedio página ${promedioHorasPagina.toFixed(1)} hs`}
                  sx={{
                    backgroundColor: alpha("#2563eb", 0.08),
                    color: "#2563eb",
                  }}
                />
                <Chip icon={<AccessTimeIcon />} label={`Página ${page} de ${totalPages}`} variant="outlined" />
              </Stack>
            </Stack>
          </Paper>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Paper elevation={0} sx={{ p: 2, border: "1px solid rgba(15, 23, 42, 0.08)" }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", xl: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", xl: "center" }}
                justifyContent="space-between"
              >
                <TextField
                  placeholder="Buscar por código o descripción"
                  value={searchText}
                  onChange={(event) => {
                    setPage(1)
                    setSearchText(event.target.value)
                  }}
                  sx={{ maxWidth: 480, width: "100%" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                    Actualizar
                  </Button>
                  <Button variant="outlined" startIcon={<AssignmentIndIcon />} onClick={() => handleOpenAsignacion()}>
                    Asignar turno
                  </Button>
                  <Button variant="contained" startIcon={<AddCircleIcon />} onClick={handleOpenCreate}>
                    Nuevo turno
                  </Button>
                </Stack>
              </Stack>

              <Box sx={{ width: "100%" }}>
                <DataGrid
                  autoHeight
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  loading={loading}
                  disableRowSelectionOnClick
                  hideFooter
                  rowHeight={52}
                  onRowDoubleClick={(params) => handleOpenEdit(params.row.id)}
                />
              </Box>

              <CustomPagination
                currentPage={page}
                totalPages={totalPages}
                totalCount={totalCount}
                vieneDe="tablaRrhhTurnos"
                onPageChange={setPage}
              />
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <ModalRrhhTurno
        open={turnoModalOpen}
        mode={turnoModalMode}
        turnoDetail={selectedTurno}
        loading={loadingDetail}
        onClose={() => setTurnoModalOpen(false)}
        onSave={handleSaveTurno}
      />

      <ModalRrhhAsignacionTurno
        open={asignacionModalOpen}
        empleadosOptions={empleadosOptions}
        turnosOptions={turnosOptions}
        defaultTurno={defaultTurnoAsignacion}
        onClose={() => setAsignacionModalOpen(false)}
        onSave={handleSaveAsignacion}
        onLoadAsignaciones={loadAsignacionesEmpleado}
      />
    </ThemeProvider>
  )
}

export default TablaRrhhTurnosComponent
